import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSession } from '@clerk/clerk-react';
import { API_URL } from 'constants/index';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { DatabaseFileLabel } from 'shared/components/UniversalPickerModal';
import {
  DueDiligenceAnswerType,
  DueDiligenceEvaluationMode,
  DueDiligenceStatusColor,
  StatusRuleType,
  ConditionGroup,
} from 'shared/types/due-diligence';
import {
  useDueDiligenceCategory,
  useCreateDueDiligenceCategory,
  useUpdateDueDiligenceCategory,
  useCreateDueDiligenceQuestion,
  useUpdateDueDiligenceQuestion,
  useCreateDueDiligenceAnswerOption,
  useUpdateDueDiligenceAnswerOption,
} from 'hooks/useDueDiligence';
import type { QuestionItem, AnswerOptionItem, CategoryFormData } from './types';
import { DEFAULT_FORM_DATA, createEmptyQuestion, createEmptyAnswerOption } from './types';

export function useCategoryEditorState() {
  const navigate = useNavigate();
  const { categoryId } = useParams<{ categoryId?: string }>();
  const { session } = useSession();
  const isEditMode = !!(categoryId && categoryId !== 'nieuw');

  // File labels for AI extraction configuration
  const [fileLabels, setFileLabels] = useState<DatabaseFileLabel[]>([]);

  // Document label picker modal state
  const [documentLabelPickerOpen, setDocumentLabelPickerOpen] = useState(false);
  const [documentLabelPickerQuestionIndex, setDocumentLabelPickerQuestionIndex] = useState<number | null>(null);

  // Form state
  const [formData, setFormData] = useState<CategoryFormData>(DEFAULT_FORM_DATA);
  const [questions, setQuestions] = useState<QuestionItem[]>([]);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());
  const [originalQuestionIds, setOriginalQuestionIds] = useState<number[]>([]);
  const [originalOptionIds, setOriginalOptionIds] = useState<Record<number, number[]>>({});

  // Clipboard for copying conditions between answer options
  const [conditionsClipboard, setConditionsClipboard] = useState<ConditionGroup | null>(null);

  // Hooks
  const { createCategory, isCreating } = useCreateDueDiligenceCategory();
  const { updateCategory, isUpdating } = useUpdateDueDiligenceCategory();
  const { createQuestion } = useCreateDueDiligenceQuestion();
  const { updateQuestion } = useUpdateDueDiligenceQuestion();
  const { createAnswerOption } = useCreateDueDiligenceAnswerOption();
  const { updateAnswerOption } = useUpdateDueDiligenceAnswerOption();

  const { category: fetchedCategory, isLoading: isFetchingCategory } = useDueDiligenceCategory(
    isEditMode ? parseInt(categoryId!) : null,
  );

  const isLoading = isCreating || isUpdating;
  const isInitialLoading = isEditMode && isFetchingCategory;

  // Initialize form data from fetched category
  useEffect(() => {
    if (fetchedCategory && isEditMode) {
      setFormData({
        name: fetchedCategory.name,
        description: fetchedCategory.description || '',
        icon: fetchedCategory.icon || '',
        scope: fetchedCategory.scope,
        displayOrder: fetchedCategory.displayOrder,
        active: fetchedCategory.active,
      });

      const questionsWithDetails: QuestionItem[] = (fetchedCategory.questions || []).map((q) => ({
        id: q.id,
        fieldName: q.fieldName,
        displayName: q.displayName,
        helpText: q.helpText,
        displayOrder: q.displayOrder,
        active: q.active,
        answerType: q.answerType || DueDiligenceAnswerType.OPTIONS,
        documentLabelId: q.documentLabelId,
        documentLabelIds: q.documentLabels?.map((l) => l.id) || (q.documentLabelId ? [q.documentLabelId] : []),
        aiExtractionHint: q.aiExtractionHint,
        evaluationMode: q.evaluationMode || DueDiligenceEvaluationMode.MANUAL,
        statusEvaluationConfig: q.statusEvaluationConfig,
        aggregationConfig: q.aggregationConfig,
        documentBehavior: q.documentBehavior || 'replacing',
        cumulativeConfig: q.cumulativeConfig || null,
        extractionValueType: q.extractionValueType || null,
        extractionPrompt: q.extractionPrompt || null,
        extractionLabel: q.extractionLabel || null,
        dependencyConfig: q.dependencyConfig || null,
        answerOptions: (q.answerOptions || []).map((opt) => ({
          id: opt.id,
          value: opt.value,
          label: opt.label,
          statusColor: opt.statusColor,
          tooltipLabel: opt.tooltipLabel,
          isDefault: opt.isDefault,
          displayOrder: opt.displayOrder,
          active: opt.active,
          conditionRoot:
            opt.conditionRoot ??
            (opt.conditions?.length
              ? {
                  id: `root_${opt.id || Date.now()}`,
                  type: 'group' as const,
                  logic: (opt.conditionLogic || 'AND') as 'AND' | 'OR',
                  children: opt.conditions,
                }
              : null),
        })),
      }));
      setQuestions(questionsWithDetails);

      setOriginalQuestionIds(
        (fetchedCategory.questions || [])
          .map((q) => q.id)
          .filter((id): id is number => id !== undefined),
      );

      const optionIds: Record<number, number[]> = {};
      (fetchedCategory.questions || []).forEach((q) => {
        optionIds[q.id] = (q.answerOptions || [])
          .map((opt) => opt.id)
          .filter((id): id is number => id !== undefined);
      });
      setOriginalOptionIds(optionIds);
    }
  }, [fetchedCategory, isEditMode]);

  // Fetch file labels
  useEffect(() => {
    const fetchFileLabels = async () => {
      if (!session) return;
      try {
        const response = await fetchWithAuth(
          `${API_URL}/file/search-labels?term=&resourceTypes=building,unit&documentTypes=document`,
          { method: 'GET' },
          session,
        );
        if (response.ok) {
          const labels: DatabaseFileLabel[] = await response.json();
          labels.sort((a, b) => a.label.localeCompare(b.label));
          setFileLabels(labels);
        }
      } catch (err) {
        console.error('Error fetching file labels:', err);
      }
    };
    fetchFileLabels();
  }, [session]);

  // --- Question handlers ---

  const handleAddQuestion = useCallback((question?: QuestionItem) => {
    const newQuestion = question || createEmptyQuestion();
    if (!newQuestion.id) newQuestion.id = Date.now();
    newQuestion.displayOrder = questions.length;
    setQuestions((prev) => [...prev, newQuestion]);
    setExpandedQuestions((prev) => new Set([...prev, newQuestion.id!]));
  }, [questions.length]);

  const handleUpdateQuestion = useCallback((index: number, updates: Partial<QuestionItem>) => {
    setQuestions((prev) => {
      const newQuestions = [...prev];
      let finalUpdates = { ...updates };

      if (updates.answerType === DueDiligenceAnswerType.DOCUMENT_EXISTS) {
        finalUpdates = {
          ...finalUpdates,
          evaluationMode: DueDiligenceEvaluationMode.FILE_BASED,
          statusEvaluationConfig: {
            statusRules: {
              logicalOperator: 'AND' as const,
              rules: [
                {
                  type: StatusRuleType.FILE_EXISTS,
                  resultStatusColor: DueDiligenceStatusColor.GREEN,
                  tooltipTemplate: 'Document aanwezig',
                },
              ],
            },
            defaultStatus: DueDiligenceStatusColor.RED,
            defaultTooltip: 'Document ontbreekt',
            aggregationStrategy: 'any' as const,
          },
          extractionValueType: null,
          extractionPrompt: null,
          extractionLabel: null,
        };
      }

      if (updates.answerType === DueDiligenceAnswerType.EXTRACTION) {
        finalUpdates = {
          ...finalUpdates,
          answerOptions: [],
          evaluationMode: DueDiligenceEvaluationMode.FILE_BASED,
          statusEvaluationConfig: null,
          extractionValueType: finalUpdates.extractionValueType || 'number',
        };
      }

      if (updates.answerType && updates.answerType !== DueDiligenceAnswerType.EXTRACTION) {
        const currentQuestion = newQuestions[index];
        if (currentQuestion.answerType === DueDiligenceAnswerType.EXTRACTION) {
          finalUpdates = {
            ...finalUpdates,
            extractionValueType: null,
            extractionPrompt: null,
            extractionLabel: null,
          };
        }
      }

      newQuestions[index] = { ...newQuestions[index], ...finalUpdates };
      return newQuestions;
    });
  }, []);

  const handleRemoveQuestion = useCallback((index: number) => {
    setQuestions((prev) => {
      const questionId = prev[index].id;
      if (questionId) {
        setExpandedQuestions((ex) => {
          const next = new Set(ex);
          next.delete(questionId);
          return next;
        });
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleMoveQuestion = useCallback((index: number, direction: 'up' | 'down') => {
    setQuestions((prev) => {
      if ((direction === 'up' && index === 0) || (direction === 'down' && index === prev.length - 1)) {
        return prev;
      }
      const newQuestions = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      [newQuestions[index], newQuestions[targetIndex]] = [newQuestions[targetIndex], newQuestions[index]];
      newQuestions.forEach((q, i) => (q.displayOrder = i));
      return newQuestions;
    });
  }, []);

  const handleDuplicateQuestion = useCallback((index: number) => {
    setQuestions((prev) => {
      const source = prev[index];
      const clone: QuestionItem = {
        ...JSON.parse(JSON.stringify(source)),
        id: Date.now(),
        fieldName: '',
        displayName: source.displayName ? `${source.displayName} (kopie)` : '',
        displayOrder: prev.length,
      };
      // Generate new IDs for answer options
      clone.answerOptions = clone.answerOptions.map((opt: AnswerOptionItem) => ({
        ...opt,
        id: undefined,
      }));
      const newQuestions = [...prev, clone];
      setExpandedQuestions((ex) => new Set([...ex, clone.id!]));
      return newQuestions;
    });
  }, []);

  const toggleQuestionExpanded = useCallback((questionId: number) => {
    setExpandedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });
  }, []);

  // --- Answer option handlers ---

  const handleAddAnswerOption = useCallback((questionIndex: number) => {
    setQuestions((prev) => {
      const newQuestions = [...prev];
      const question = newQuestions[questionIndex];
      const newOption = createEmptyAnswerOption(question.answerOptions.length === 0);
      newOption.displayOrder = question.answerOptions.length;
      question.answerOptions = [...question.answerOptions, newOption];
      return newQuestions;
    });
  }, []);

  const handleUpdateAnswerOption = useCallback(
    (questionIndex: number, optionIndex: number, updates: Partial<AnswerOptionItem>) => {
      setQuestions((prev) => {
        const newQuestions = [...prev];
        const options = [...newQuestions[questionIndex].answerOptions];
        options[optionIndex] = { ...options[optionIndex], ...updates };

        if (updates.isDefault === true) {
          options.forEach((opt, i) => {
            if (i !== optionIndex) opt.isDefault = false;
          });
        }

        newQuestions[questionIndex] = { ...newQuestions[questionIndex], answerOptions: options };
        return newQuestions;
      });
    },
    [],
  );

  const handleRemoveAnswerOption = useCallback((questionIndex: number, optionIndex: number) => {
    setQuestions((prev) => {
      const newQuestions = [...prev];
      newQuestions[questionIndex] = {
        ...newQuestions[questionIndex],
        answerOptions: newQuestions[questionIndex].answerOptions.filter((_, i) => i !== optionIndex),
      };
      return newQuestions;
    });
  }, []);

  // --- Condition clipboard ---

  const cloneConditionGroup = (group: ConditionGroup): ConditionGroup => {
    const generateNewId = () => `cond_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const cloneNode = (node: any): any => {
      if (node.type === 'group') {
        return { ...node, id: generateNewId(), children: node.children.map(cloneNode) };
      }
      return { ...node, id: generateNewId() };
    };
    return cloneNode(group);
  };

  const handleCopyConditions = useCallback(
    (questionIndex: number, optionIndex: number) => {
      const option = questions[questionIndex]?.answerOptions[optionIndex];
      if (option?.conditionRoot) {
        setConditionsClipboard(option.conditionRoot);
        toast.success('Voorwaarden gekopieerd naar klembord');
      } else {
        toast.info('Geen voorwaarden om te kopiëren');
      }
    },
    [questions],
  );

  const handlePasteConditions = useCallback(
    (questionIndex: number, optionIndex: number) => {
      if (!conditionsClipboard) {
        toast.info('Geen voorwaarden op klembord');
        return;
      }
      const cloned = cloneConditionGroup(conditionsClipboard);
      handleUpdateAnswerOption(questionIndex, optionIndex, { conditionRoot: cloned });
      toast.success('Voorwaarden geplakt');
    },
    [conditionsClipboard, handleUpdateAnswerOption],
  );

  // --- Document label picker ---

  const openDocumentLabelPicker = useCallback((questionIndex: number) => {
    setDocumentLabelPickerQuestionIndex(questionIndex);
    setDocumentLabelPickerOpen(true);
  }, []);

  const closeDocumentLabelPicker = useCallback(() => {
    setDocumentLabelPickerOpen(false);
    setDocumentLabelPickerQuestionIndex(null);
  }, []);

  const handleDocumentLabelSelected = useCallback(
    (selectedLabel: { id: number } | null) => {
      if (documentLabelPickerQuestionIndex !== null && selectedLabel) {
        const currentLabels = questions[documentLabelPickerQuestionIndex]?.documentLabelIds || [];
        if (!currentLabels.includes(selectedLabel.id)) {
          handleUpdateQuestion(documentLabelPickerQuestionIndex, {
            documentLabelIds: [...currentLabels, selectedLabel.id],
          });
        }
      }
      closeDocumentLabelPicker();
    },
    [documentLabelPickerQuestionIndex, questions, handleUpdateQuestion, closeDocumentLabelPicker],
  );

  // --- Submit ---

  const handleSubmit = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error('Voer een categorienaam in');
        return;
      }

      const buildQuestionPayload = (question: QuestionItem) => ({
        fieldName:
          question.fieldName ||
          question.displayName
            .toLowerCase()
            .replace(/\s+/g, '_')
            .replace(/[^a-z0-9_]/g, ''),
        displayName: question.displayName,
        helpText: question.helpText,
        displayOrder: question.displayOrder,
        answerType: question.answerType,
        documentLabelIds: question.documentLabelIds,
        aiExtractionHint: question.aiExtractionHint,
        evaluationMode: question.evaluationMode,
        statusEvaluationConfig: question.statusEvaluationConfig || undefined,
        documentBehavior: question.documentBehavior || 'replacing',
        cumulativeConfig: question.cumulativeConfig || undefined,
        extractionValueType: question.extractionValueType || undefined,
        extractionPrompt: question.extractionPrompt || undefined,
        extractionLabel: question.extractionLabel || undefined,
        dependencyConfig: question.dependencyConfig || undefined,
      });

      const buildOptionPayload = (option: AnswerOptionItem) => ({
        value:
          option.value ||
          option.label
            .toLowerCase()
            .replace(/\s+/g, '_')
            .replace(/[^a-z0-9_]/g, ''),
        label: option.label,
        statusColor: option.statusColor,
        tooltipLabel: option.tooltipLabel,
        isDefault: option.isDefault,
        displayOrder: option.displayOrder,
        conditionRoot: option.conditionRoot,
      });

      if (!isEditMode) {
        const createdCategory = await createCategory({
          name: formData.name,
          description: formData.description || undefined,
          icon: formData.icon || undefined,
          scope: formData.scope,
          displayOrder: formData.displayOrder,
        });

        for (const question of questions) {
          if (!question.displayName.trim()) continue;
          const createdQuestion = await createQuestion(createdCategory.id, buildQuestionPayload(question));
          for (const option of question.answerOptions) {
            if (!option.label.trim()) continue;
            await createAnswerOption(createdCategory.id, createdQuestion.id, buildOptionPayload(option));
          }
        }

        toast.success('Due diligence categorie succesvol aangemaakt');
      } else {
        await updateCategory(parseInt(categoryId!), {
          name: formData.name,
          description: formData.description,
          icon: formData.icon || undefined,
          scope: formData.scope,
          displayOrder: formData.displayOrder,
          active: formData.active,
        });

        for (const question of questions) {
          let questionId: number;

          if (question.id && originalQuestionIds.includes(question.id)) {
            await updateQuestion(parseInt(categoryId!), question.id, {
              ...buildQuestionPayload(question),
              statusEvaluationConfig: question.statusEvaluationConfig,
              cumulativeConfig: question.cumulativeConfig,
              extractionValueType: question.extractionValueType,
              extractionPrompt: question.extractionPrompt,
              extractionLabel: question.extractionLabel,
              dependencyConfig: question.dependencyConfig,
            });
            questionId = question.id;
          } else if (question.displayName.trim()) {
            const createdQuestion = await createQuestion(parseInt(categoryId!), buildQuestionPayload(question));
            questionId = createdQuestion.id;
          } else {
            continue;
          }

          const originalOpts = question.id ? originalOptionIds[question.id] || [] : [];
          for (const option of question.answerOptions) {
            if (option.id && originalOpts.includes(option.id)) {
              await updateAnswerOption(parseInt(categoryId!), questionId, option.id, buildOptionPayload(option));
            } else if (option.label.trim()) {
              await createAnswerOption(parseInt(categoryId!), questionId, buildOptionPayload(option));
            }
          }
        }

        toast.success('Due diligence categorie succesvol bijgewerkt');
      }

      navigate('/settings#dueDiligence');
    } catch (error) {
      console.error('[DueDiligence] Error saving due diligence category:', error);
      toast.error(error instanceof Error ? error.message : 'Kan due diligence categorie niet opslaan');
      throw error; // Re-throw so Sentry catches it
    }
  };

  return {
    // State
    formData,
    setFormData,
    questions,
    setQuestions,
    expandedQuestions,
    fileLabels,
    conditionsClipboard,
    isEditMode,
    isLoading,
    isInitialLoading,
    fetchedCategory,
    categoryId,

    // Document label picker
    documentLabelPickerOpen,
    documentLabelPickerQuestionIndex,
    openDocumentLabelPicker,
    closeDocumentLabelPicker,
    handleDocumentLabelSelected,

    // Question handlers
    handleAddQuestion,
    handleUpdateQuestion,
    handleRemoveQuestion,
    handleMoveQuestion,
    handleDuplicateQuestion,
    toggleQuestionExpanded,

    // Answer option handlers
    handleAddAnswerOption,
    handleUpdateAnswerOption,
    handleRemoveAnswerOption,

    // Condition clipboard
    handleCopyConditions,
    handlePasteConditions,

    // Submit
    handleSubmit,

    // Navigation
    navigate,
  };
}
